import React, { FC, ReactElement } from 'react';

import { useTranslation } from 'react-i18next';
import { Controller, UseControllerReturn, UseFormReturn } from 'react-hook-form';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import ConnectForm from '../../../../common/reactHookForm/ConnectForm';
import {
  TrashcanConfigModel,
  WasteType,
  PricingType,
  PlacementType,
} from '@thingslog/repositories';

const TrashCanConfiguration: FC<TrashCanConfigurationProps> = () => {
  const { t } = useTranslation();

  return (
    <ConnectForm<TrashcanConfigModel>>
      {({ register, control }: UseFormReturn<TrashcanConfigModel>): ReactElement => (
        <div className="grid grid-cols-4 max-xl:grid-cols-3 max-lg:grid-cols-2 gap-5">
          {/* Basic Identification */}
          <TextField
            {...register('brand')}
            label={t('device_initial_config_trashcan_brand')}
            size="small"
          />
          <TextField
            {...register('model')}
            label={t('device_initial_config_trashcan_model')}
            size="small"
          />

          {/* RFID Tag */}
          <Controller
            control={control}
            name="rfidTag"
            render={({
              field,
            }: UseControllerReturn<TrashcanConfigModel, 'rfidTag'>): ReactElement => (
              <>
                <TextField
                  value={field.value?.epc ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    field.onChange({
                      ...(field.value ?? { tid: '' }),
                      epc: e.target.value,
                    });
                  }}
                  label={'EPC'}
                  size="small"
                />
                <TextField
                  value={field.value?.tid ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    field.onChange({
                      ...(field.value ?? { epc: '' }),
                      tid: e.target.value,
                    });
                  }}
                  label={'TID'}
                  size="small"
                />
              </>
            )}
          />

          {/* Physical Capacity */}
          <TextField
            {...register('volume', { valueAsNumber: true })}
            type="number"
            label={t('device_initial_config_trashcan_volume')}
            size="small"
          />
          <TextField
            {...register('maxWeight', {
              valueAsNumber: true,
              min: { value: 0, message: 'Must be a positive number' },
            })}
            type="number"
            inputProps={{ min: 0 }}
            label={t('device_initial_config_trashcan_max_weight')}
            size="small"
          />

          {/* Physical Appearance */}
          <TextField
            {...register('material')}
            label={t('device_initial_config_trashcan_material')}
            size="small"
          />
          <TextField
            {...register('color')}
            label={t('device_initial_config_trashcan_color')}
            size="small"
          />

          {/* Waste Classification */}
          <FormControl size="small">
            <InputLabel>{t('device_initial_config_trashcan_waste_type')}</InputLabel>
            <Controller
              control={control}
              name="wasteType"
              render={({
                field,
              }: UseControllerReturn<TrashcanConfigModel, 'wasteType'>): ReactElement => (
                <Select<WasteType | null>
                  {...field}
                  label={t('device_initial_config_trashcan_waste_type')}
                >
                  <MenuItem value="PLASTIC">
                    {t('device_initial_config_trashcan_waste_type_plastic')}
                  </MenuItem>
                  <MenuItem value="BIO">
                    {t('device_initial_config_trashcan_waste_type_bio')}
                  </MenuItem>
                  <MenuItem value="PAPER">
                    {t('device_initial_config_trashcan_waste_type_paper')}
                  </MenuItem>
                  <MenuItem value="GLASS">
                    {t('device_initial_config_trashcan_waste_type_glass')}
                  </MenuItem>
                  <MenuItem value="METAL">
                    {t('device_initial_config_trashcan_waste_type_metal')}
                  </MenuItem>
                  <MenuItem value="MIXED">
                    {t('device_initial_config_trashcan_waste_type_mixed')}
                  </MenuItem>
                  <MenuItem value="OTHER">
                    {t('device_initial_config_trashcan_waste_type_other')}
                  </MenuItem>
                </Select>
              )}
            />
          </FormControl>

          {/* Business/Operational Settings */}
          <FormControl size="small">
            <InputLabel>{t('device_initial_config_trashcan_pricing')}</InputLabel>
            <Controller
              control={control}
              name="pricing"
              render={({
                field,
              }: UseControllerReturn<TrashcanConfigModel, 'pricing'>): ReactElement => (
                <Select<PricingType | null>
                  {...field}
                  label={t('device_initial_config_trashcan_pricing')}
                >
                  <MenuItem value="FREE">
                    {t('device_initial_config_trashcan_pricing_free')}
                  </MenuItem>
                  <MenuItem value="PER_LIFT">
                    {t('device_initial_config_trashcan_pricing_per_lift')}
                  </MenuItem>
                  <MenuItem value="PER_KG">
                    {t('device_initial_config_trashcan_pricing_per_kg')}
                  </MenuItem>
                  <MenuItem value="PER_SUBSCRIPTION">
                    {t('device_initial_config_trashcan_pricing_per_subscription')}
                  </MenuItem>
                </Select>
              )}
            />
          </FormControl>

          <FormControl size="small">
            <InputLabel>{t('device_initial_config_trashcan_placement')}</InputLabel>
            <Controller
              control={control}
              name="placement"
              render={({
                field,
              }: UseControllerReturn<TrashcanConfigModel, 'placement'>): ReactElement => (
                <Select<PlacementType | null>
                  {...field}
                  label={t('device_initial_config_trashcan_placement')}
                >
                  <MenuItem value="ON_THE_STREET">
                    {t('device_initial_config_trashcan_placement_on_the_street')}
                  </MenuItem>
                  <MenuItem value="INSIDE">
                    {t('device_initial_config_trashcan_placement_inside')}
                  </MenuItem>
                  <MenuItem value="BACKYARD">
                    {t('device_initial_config_trashcan_placement_backyard')}
                  </MenuItem>
                </Select>
              )}
            />
          </FormControl>

          {/* Customer Association */}
          <TextField
            {...register('customerCommunityId')}
            label={t('device_initial_config_trashcan_customer_community_id')}
            size="small"
          />
        </div>
      )}
    </ConnectForm>
  );
};

interface TrashCanConfigurationProps {}

export default TrashCanConfiguration;
